import React, {useEffect, useState} from "react";
import 'react-toastify/dist/ReactToastify.css';
import {connect} from "react-redux";
import * as PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import TeacherForm from "./TeacherForm";
import {STATUS} from "../common/Const";
import { history } from "../../redux/store/configureStore";
import * as teacherActions from "../../redux/actions/teacherActions";
import {validatePerson} from "../common/Validations";
import {hasErrors, hasNoErrors} from "../common/Helpers";
import {
  buildManageRouteWithSchoolYear,
  getNormalizedSchoolYearStartYearFromSearch,
} from "./manageSchoolYearRouting";

export function Teacher({teachers, loadTeachers, saveTeacher, archiveTeacher, ...props}) {
  const location = useLocation();
  const activeSchoolYearStartYear = getNormalizedSchoolYearStartYearFromSearch(location.search);
  const [teacher, setTeacher] = useState({ ...props.teacher });
  const [initialTeacher, setInitialTeacher] = useState({ ...props.teacher });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState(STATUS.IDLE);

  useEffect(() => {
    if (teachers.length === 0) {
      loadTeachers().catch((error) => {
        alert(`Loading teachers failed ${error}`);
      });
    } else {
      setTeacher({...props.teacher});
      setInitialTeacher({...props.teacher});
      setTouched({});
      setStatus(props.teacher && props.teacher.id ? STATUS.VIEW : STATUS.IDLE);
    }
  }, [props.teacher]);

  const validate = () => {
    // eslint-disable-next-line no-shadow
    const errors = validatePerson(teacher);
    setErrors(errors);
    return hasNoErrors(errors);
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched({ ...touched, [name]: true });
    validate();
  };

  const handleCancel = () => {
    history.push(buildManageRouteWithSchoolYear("/manage/teachers", "", activeSchoolYearStartYear));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const updatedTeacher = {...teacher, [name]: value};
    setTeacher(updatedTeacher);
    setTouched({ ...touched, [name]: true });
    setErrors(validatePerson(updatedTeacher));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus(STATUS.SUBMITTING);
    const isValid = validate();

    if (isValid) {
      saveTeacher(teacher)
        .then((savedTeacher) => {
          if (savedTeacher) {
            setTeacher(savedTeacher);
            setInitialTeacher(savedTeacher);
          }
          setStatus(STATUS.VIEW);
          setTouched({});
          toast.success('Dane zostały zapisane');
          handleCancel();
        })
        .catch((error) => {
          setErrors({...errors, saveError: error.message});
          setStatus(STATUS.SUBMITTED);
        });
    } else {
      setStatus(STATUS.SUBMITTED);
    }
  };

  const handleArchive = () => {
    setStatus(STATUS.SUBMITTING);
    archiveTeacher(teacher)
      .then((savedTeacher) => {
        setTeacher(savedTeacher);
        setInitialTeacher(savedTeacher);
        setTouched({});
        setStatus(STATUS.IDLE);
      })
      .catch((error) => {
        setErrors({ ...errors, saveError: error.message });
        setStatus(STATUS.SUBMITTED);
      });
  };

  const disabled = status === STATUS.SUBMITTING;
  const teacherFields = ["firstName", "lastName", "email", "mobile"];
  const isModified = teacherFields.some(field => (teacher[field] || "") !== (initialTeacher[field] || ""));

  return (
    <div className="mb-5">
      { hasErrors(errors) && status === STATUS.SUBMITTED && (
        <div role="alert" className="alert alert-danger">
            {Object.keys(errors).map(key => <div key={key}>{errors[key]}</div>)}
        </div>
      )}
      <TeacherForm
        firstName={teacher.firstName}
        lastName={teacher.lastName}
        email={teacher.email}
        mobile={teacher.mobile}
        touched={touched}
        errors={errors}
        onBlur={handleBlur}
        onChange={handleChange}
        onSubmit={handleSubmit}
        status={status}
      />
      <div className="row content mt-3">
        {isModified && !teacher.archived && (
          <div className="col-md-block mb-2">
            <button type="button" className="btn manage-button manage-red-button ml-2" disabled={disabled} onClick={handleSubmit}>
              {disabled ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        )}
        <div className="col-md-block mr-auto pl-2 mb-2">
          <button type="button" className="btn manage-button manage-teal-button" disabled={disabled} onClick={handleCancel}>Powrót</button>
        </div>
        {teacher.id && (
          teacher.archived
            ? <button type="button" className="btn manage-button btn-success ml-2 mr-2 mb-2" disabled={disabled} onClick={handleArchive}>Przywróć</button>
            : <button type="button" className="btn manage-button btn-success ml-2 mr-2 mb-2" disabled={disabled} onClick={handleArchive}>Zarchiwizuj</button>
        )}
      </div>
    </div>
  );
}

Teacher.propTypes = {
  teachers: PropTypes.array.isRequired,
  teacher: PropTypes.object,
  loadTeachers: PropTypes.func.isRequired,
  saveTeacher: PropTypes.func.isRequired,
  archiveTeacher: PropTypes.func.isRequired,
};

const newTeacher = {};

const getTeacherById = (teachers, id) => teachers.find(teacher => teacher.id === id) || null;

const mapStateToProps = (state, ownProps) => {
  const {id} = ownProps.match.params;
  const teacher =
    id === "new" || state.teachers.length === 0
      ? newTeacher
      : getTeacherById(state.teachers, id);

  return {
    teacher,
    teachers: state.teachers,
  };
};

const mapDispatchToProps = dispatch => ({
  loadTeachers: () => dispatch(teacherActions.loadTeachers()),
  saveTeacher: teacher => dispatch(teacherActions.saveTeacher(teacher)),
  archiveTeacher: teacher => dispatch(teacherActions.archiveTeacher(teacher)),
});

Teacher.defaultProps = {
  teacher: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(Teacher);
